import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  collection,
  collectionGroup,
  getDocs,
} from "firebase/firestore";
import { firestoreProd } from "../../firebase";
import {
  FaSearch,
  FaSync,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaBookOpen,
  FaBuilding,
  FaTrophy,
  FaCalendarWeek,
  FaChevronDown,
  FaChevronRight,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ROWS_PER_PAGE = 25;

const CustomInstructionsPage = ({ onReady }) => {
  const [instructions, setInstructions] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrg, setSelectedOrg] = useState("");

  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");

  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const fetchInstructions = useCallback(async () => {
    if (!firestoreProd) {
      toast.error("Firebase is not initialized");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const [orgsSnapshot, usersSnapshot, taskSnapshot, familySnapshot, globalSnapshot] =
        await Promise.all([
          getDocs(collection(firestoreProd, "organizations")),
          getDocs(collection(firestoreProd, "users")),
          getDocs(
            collectionGroup(
              firestoreProd,
              "processingChecklistTaskInstructions"
            )
          ),
          getDocs(
            collectionGroup(
              firestoreProd,
              "processingChecklistFamilyInstructions"
            )
          ),
          getDocs(
            collectionGroup(
              firestoreProd,
              "processingChecklistGlobalInstructions"
            )
          ),
        ]);

      const orgNames = {};
      orgsSnapshot.forEach((orgDoc) => {
        orgNames[orgDoc.id] = orgDoc.data().name || orgDoc.id;
      });

      const internalUserIds = new Set();
      usersSnapshot.forEach((userDoc) => {
        const email = (userDoc.data().email || "").toLowerCase();
        if (email.endsWith("@addy-ai.com") || email.endsWith("@addy.com")) {
          internalUserIds.add(userDoc.id);
        }
      });

      const allInstructions = [];

      taskSnapshot.forEach((docSnap) => {
        const orgId = docSnap.ref.parent.parent.id;
        const taskId = docSnap.id;
        const instArr = docSnap.data().instructions || [];
        instArr.forEach((inst) => {
          if (internalUserIds.has(inst.createdBy)) return;
          allInstructions.push({
            ...inst,
            taskId: inst.taskId || taskId,
            organizationId: orgId,
            organizationName: orgNames[orgId] || orgId,
          });
        });
      });

      familySnapshot.forEach((docSnap) => {
        const orgId = docSnap.ref.parent.parent.id;
        const taskFamily = docSnap.id;
        const instArr = docSnap.data().instructions || [];
        instArr.forEach((inst) => {
          if (internalUserIds.has(inst.createdBy)) return;
          allInstructions.push({
            ...inst,
            taskFamily: inst.taskFamily || taskFamily,
            scope: inst.scope || "allLoans",
            organizationId: orgId,
            organizationName: orgNames[orgId] || orgId,
          });
        });
      });

      globalSnapshot.forEach((docSnap) => {
        const orgId = docSnap.ref.parent.parent.id;
        const instArr = docSnap.data().instructions || [];
        instArr.forEach((inst) => {
          if (internalUserIds.has(inst.createdBy)) return;
          allInstructions.push({
            ...inst,
            taskId: inst.taskId || inst.taskIds?.[0] || "",
            scope: inst.scope || "loanType",
            organizationId: orgId,
            organizationName: orgNames[orgId] || orgId,
          });
        });
      });

      allInstructions.sort(
        (a, b) =>
          new Date(b.createdAt || 0).getTime() -
          new Date(a.createdAt || 0).getTime()
      );

      const orgCounts = {};
      allInstructions.forEach((inst) => {
        orgCounts[inst.organizationId] =
          (orgCounts[inst.organizationId] || 0) + 1;
      });

      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const thirtyDaysAgo = new Date(
        now.getTime() - 30 * 24 * 60 * 60 * 1000
      );
      const topOrgEntry = Object.entries(orgCounts).sort(
        (a, b) => b[1] - a[1]
      )[0];

      setInstructions(allInstructions);
      setMetrics({
        totalInstructions: allInstructions.length,
        orgsWithInstructions: Object.keys(orgCounts).length,
        topOrg: topOrgEntry
          ? {
              id: topOrgEntry[0],
              name: orgNames[topOrgEntry[0]] || topOrgEntry[0],
              count: topOrgEntry[1],
            }
          : null,
        last7Days: allInstructions.filter(
          (i) => new Date(i.createdAt) >= sevenDaysAgo
        ).length,
        last30Days: allInstructions.filter(
          (i) => new Date(i.createdAt) >= thirtyDaysAgo
        ).length,
      });
      setOrganizations(
        Object.entries(orgCounts)
          .map(([id, count]) => ({ id, name: orgNames[id] || id, count }))
          .sort((a, b) => b.count - a.count)
      );

      toast.success(`Loaded ${allInstructions.length} instructions`);
    } catch (err) {
      console.error("Error fetching custom instructions:", err);
      setError(err.message);
      toast.error("Failed to fetch custom instructions");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInstructions();
    onReady?.();
  }, [fetchInstructions, onReady]);

  const filteredAndSorted = useMemo(() => {
    let result = [...instructions];

    if (selectedOrg) {
      result = result.filter((i) => i.organizationId === selectedOrg);
    }

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        (i) =>
          (i.organizationName || "").toLowerCase().includes(search) ||
          (i.createdByName || "").toLowerCase().includes(search) ||
          (i.title || "").toLowerCase().includes(search) ||
          (i.description || "").toLowerCase().includes(search) ||
          (i.loanTypeName || "").toLowerCase().includes(search) ||
          (i.taskTitle || "").toLowerCase().includes(search)
      );
    }

    result.sort((a, b) => {
      let aVal = a[sortField] || "";
      let bVal = b[sortField] || "";
      if (sortField === "createdAt") {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDirection === "asc" ? cmp : -cmp;
    });

    return result;
  }, [
    instructions,
    selectedOrg,
    searchTerm,
    sortField,
    sortDirection,
  ]);

  const totalPages = Math.ceil(filteredAndSorted.length / ROWS_PER_PAGE);
  const paginatedData = filteredAndSorted.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedOrg]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection(field === "createdAt" ? "desc" : "asc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field)
      return (
        <FaSort className="ms-1 text-muted" style={{ fontSize: "11px" }} />
      );
    return sortDirection === "asc" ? (
      <FaSortUp
        className="ms-1"
        style={{ fontSize: "11px", color: "#7f7fd5" }}
      />
    ) : (
      <FaSortDown
        className="ms-1"
        style={{ fontSize: "11px", color: "#7f7fd5" }}
      />
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="CustomInstructionsPageContainer container-fluid p-4">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        theme="light"
      />

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="mb-1">Custom Instructions</h1>
          <small className="text-muted">
            Track how organizations customize their AI agent with training
            instructions
          </small>
        </div>
        <button
          className="btn btn-purple d-flex align-items-center gap-2"
          onClick={fetchInstructions}
          disabled={isLoading}
        >
          <FaSync className={isLoading ? "ci-spin" : ""} />
          {isLoading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {/* Metric Cards */}
      {metrics && (
        <div className="row mb-4 g-3">
          <div className="col-md-3">
            <div className="ci-metric-card">
              <div className="ci-metric-icon" style={{ background: "#eef2ff" }}>
                <FaBookOpen style={{ color: "#6366f1" }} />
              </div>
              <div>
                <div className="ci-metric-value">
                  {metrics.totalInstructions}
                </div>
                <div className="ci-metric-label">Total Instructions</div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="ci-metric-card">
              <div className="ci-metric-icon" style={{ background: "#f0fdf4" }}>
                <FaBuilding style={{ color: "#16a34a" }} />
              </div>
              <div>
                <div className="ci-metric-value">
                  {metrics.orgsWithInstructions}
                </div>
                <div className="ci-metric-label">Organizations Using</div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="ci-metric-card">
              <div className="ci-metric-icon" style={{ background: "#fef3c7" }}>
                <FaTrophy style={{ color: "#d97706" }} />
              </div>
              <div>
                <div className="ci-metric-value ci-metric-value-sm">
                  {metrics.topOrg ? `${metrics.topOrg.name}` : "N/A"}
                </div>
                <div className="ci-metric-label">
                  Top Org
                  {metrics.topOrg
                    ? ` (${metrics.topOrg.count} instructions)`
                    : ""}
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="ci-metric-card">
              <div className="ci-metric-icon" style={{ background: "#fce7f3" }}>
                <FaCalendarWeek style={{ color: "#db2777" }} />
              </div>
              <div>
                <div className="ci-metric-value">{metrics.last7Days}</div>
                <div className="ci-metric-label">
                  Last 7 Days ({metrics.last30Days} in 30d)
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="ci-filter-bar mb-4">
        <div className="row g-3 align-items-center">
          <div className="col-md-4">
            <div className="input-group">
              <span className="input-group-text">
                <FaSearch />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search by org, user, title, description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setSearchTerm("")}
                >
                  &times;
                </button>
              )}
            </div>
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={selectedOrg}
              onChange={(e) => setSelectedOrg(e.target.value)}
            >
              <option value="">All Organizations</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name} ({org.count})
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-5 d-flex align-items-center">
            <small className="text-muted">
              Showing {filteredAndSorted.length} of {instructions.length}{" "}
              instructions
            </small>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Loading */}
      {isLoading && instructions.length === 0 && (
        <div className="text-center py-5">
          <div
            className="spinner-border"
            role="status"
            style={{ color: "#7f7fd5" }}
          />
          <p className="mt-3 text-muted">Loading custom instructions...</p>
        </div>
      )}

      {/* Table */}
      {paginatedData.length > 0 && (
        <div className="card ci-table-card">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th style={{ width: 40 }}></th>
                  <th
                    className="ci-th-sortable"
                    onClick={() => handleSort("organizationName")}
                  >
                    Organization <SortIcon field="organizationName" />
                  </th>
                  <th
                    className="ci-th-sortable"
                    onClick={() => handleSort("createdByName")}
                  >
                    User <SortIcon field="createdByName" />
                  </th>
                  <th
                    className="ci-th-sortable"
                    onClick={() => handleSort("title")}
                  >
                    Title <SortIcon field="title" />
                  </th>
                  <th
                    className="ci-th-sortable"
                    onClick={() => handleSort("loanTypeName")}
                  >
                    Loan Type <SortIcon field="loanTypeName" />
                  </th>
                  <th
                    className="ci-th-sortable"
                    onClick={() => handleSort("taskTitle")}
                  >
                    Task <SortIcon field="taskTitle" />
                  </th>
                  <th
                    className="ci-th-sortable"
                    onClick={() => handleSort("createdAt")}
                  >
                    Created <SortIcon field="createdAt" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((inst, idx) => {
                  const rowKey = inst.id || `${inst.organizationId}-${idx}`;
                  const isExpanded = expandedRows.has(rowKey);
                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        onClick={() =>
                          setExpandedRows((prev) => {
                            const next = new Set(prev);
                            if (next.has(rowKey)) next.delete(rowKey);
                            else next.add(rowKey);
                            return next;
                          })
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <td>
                          {isExpanded ? (
                            <FaChevronDown className="text-muted" />
                          ) : (
                            <FaChevronRight className="text-muted" />
                          )}
                        </td>
                        <td>
                          <span className="fw-semibold">
                            {inst.organizationName}
                          </span>
                        </td>
                        <td>{inst.createdByName || "Unknown"}</td>
                        <td>
                          <span className="ci-title-cell">{inst.title}</span>
                          {inst.enabled === false && (
                            <span
                              className="badge bg-secondary ms-1"
                              style={{ fontSize: "10px" }}
                            >
                              disabled
                            </span>
                          )}
                        </td>
                        <td>
                          <span className="ci-loan-type-badge">
                            {inst.loanTypeName || inst.loanType || "N/A"}
                          </span>
                        </td>
                        <td>
                          <span className="ci-task-title">
                            {inst.taskTitle || "N/A"}
                          </span>
                        </td>
                        <td>
                          <small className="text-muted">
                            {formatDate(inst.createdAt)}
                          </small>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan="7" className="ci-expanded-cell">
                            <div className="ci-expanded-content">
                              <div className="row">
                                <div className="col-md-6">
                                  <div className="ci-detail-group">
                                    <div className="ci-detail-label">Title</div>
                                    <div className="ci-detail-value fw-semibold">
                                      {inst.title}
                                    </div>
                                  </div>
                                  <div className="ci-detail-group">
                                    <div className="ci-detail-label">
                                      Description
                                    </div>
                                    <div className="ci-detail-value ci-detail-description">
                                      {inst.description || "No description"}
                                    </div>
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <div className="ci-detail-group">
                                    <div className="ci-detail-label">
                                      Loan Type
                                    </div>
                                    <div className="ci-detail-value">
                                      {inst.loanTypeName ||
                                        inst.loanType ||
                                        "N/A"}
                                    </div>
                                  </div>
                                  <div className="ci-detail-group">
                                    <div className="ci-detail-label">Task</div>
                                    <div className="ci-detail-value">
                                      {inst.taskTitle || "N/A"}
                                    </div>
                                  </div>
                                  <div className="ci-detail-group">
                                    <div className="ci-detail-label">
                                      Created By
                                    </div>
                                    <div className="ci-detail-value">
                                      {inst.createdByName || "Unknown"} &middot;{" "}
                                      {formatDate(inst.createdAt)}
                                    </div>
                                  </div>
                                  <div className="ci-detail-group">
                                    <div className="ci-detail-label">
                                      Status
                                    </div>
                                    <div className="ci-detail-value">
                                      <span
                                        className={`badge ${inst.enabled === false ? "bg-secondary" : "bg-success"}`}
                                      >
                                        {inst.enabled === false
                                          ? "Disabled"
                                          : "Enabled"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="ci-pagination">
              <small className="text-muted">
                Page {currentPage} of {totalPages} &middot;{" "}
                {filteredAndSorted.length} total
              </small>
              <div className="d-flex gap-1">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                >
                  First
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  Prev
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  Next
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty states */}
      {!isLoading && instructions.length === 0 && !error && (
        <div className="alert alert-info">
          No custom instructions found across any organization.
        </div>
      )}

      {filteredAndSorted.length === 0 &&
        instructions.length > 0 &&
        !isLoading && (
          <div className="alert alert-info">
            <FaSearch className="me-2" />
            No instructions match your current filters. Try adjusting your
            search or filter criteria.
          </div>
        )}
    </div>
  );
};

export default CustomInstructionsPage;
